// La région d'une offre, lue dans son lieu en texte libre (lot 2 de la refonte,
// revue « Radar, page blanche » : « la région, l'angle mort des trois
// propositions »).
//
// LE LIEU N'EST PAS STRUCTURÉ. Mesure du 2026-10-11 sur radar.db : 497 lieux
// distincts pour 2 906 offres ouvertes, en trois langues (« Brussel, Belgium,
// 1000 », « BE-Brussels Hoofdstedelijk Gewest-Bruxelles », « Mamer (LU) »,
// « Rue Phocas Lejeune 23, 5032 Gembloux »), et un quart des offres sans lieu
// du tout. On lit donc dans cet ordre : le code postal belge, les noms de
// communes et de provinces, puis le nom générique de Bruxelles en dernier
// (« Zaventem, Brussels Airport » est en Flandre).
//
// UNE OFFRE DONT ON NE SAIT RIEN PASSE TOUJOURS. Le filtre écarte ce qu'on sait
// être ailleurs, jamais ce qu'on ignore : un lieu vide, « 2 sites » ou une
// ville inconnue reviennent `null`, et `dansRegions` les laisse passer. Sans
// cette règle, un quart des offres disparaîtrait des mails sans que personne
// ne sache pourquoi.

export const REGIONS = ['bruxelles', 'wallonie', 'flandre', 'luxembourg', 'etranger'] as const
export type Region = (typeof REGIONS)[number]

const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** Une liste de noms en expression régulière, bornée par des non-lettres. */
const mots = (liste: string[]) => new RegExp(`(^|[^a-z])(${liste.map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})($|[^a-z])`)

const BRUXELLES_COMMUNES = mots([
  'anderlecht', 'ixelles', 'elsene', 'etterbeek', 'schaerbeek', 'schaarbeek', 'uccle', 'ukkel', 'woluwe', 'evere',
  'forest', 'vorst', 'jette', 'molenbeek', 'saint-gilles', 'sint-gillis', 'auderghem', 'oudergem', 'watermael',
  'boitsfort', 'laeken', 'laken', 'koekelberg', 'ganshoren', 'berchem-sainte-agathe', 'haren', 'neder-over-heembeek',
  'saint-josse', 'sint-joost', 'hoofdstedelijk', 'region de bruxelles', 'brussels-capital', 'brussels capital',
])
const BRUXELLES_GENERIQUE = mots(['bruxelles', 'brussels', 'brussel', 'brussel-stad'])

const WALLONIE = mots([
  'wallonie', 'wallonia', 'walloon', 'wallon', 'hainaut', 'henegouwen', 'brabant wallon', 'waals-brabant',
  'liege', 'luik', 'namur', 'namen', 'charleroi', 'mons', 'tournai', 'doornik', 'mouscron', 'moeskroen',
  'la louviere', 'wavre', 'waver', 'louvain-la-neuve', 'ottignies', "braine-l'alleud", "braine l'alleud",
  'braine-le-comte', 'waterloo', 'nivelles', 'nijvel', 'gembloux', 'arlon', 'aarlen', 'verviers', 'seraing',
  'herstal', 'huy', 'hoei', 'gosselies', 'mont-saint-guibert', 'rixensart', 'genval', 'la hulpe', 'lasne', 'ath',
  'soignies', 'enghien', 'tubize', 'rebecq', 'jodoigne', 'perwez', 'court-saint-etienne', 'fleurus',
  'marche-en-famenne', 'bastogne', 'dinant', 'ciney', 'spa', 'eupen', 'malmedy', 'saint-vith', 'sankt vith',
  'waremme', 'ans', 'flemalle', 'chaudfontaine', 'sambreville', 'andenne', 'jambes', 'binche', 'peruwelz',
  'comines', 'lessines', 'frameries', 'saint-ghislain', 'seneffe', 'manage', 'courcelles', 'thuin', 'chimay',
  'couvin', 'philippeville', 'chatelet', 'jumet', 'marcinelle', 'libramont', 'neufchateau', 'virton', 'aubange',
  'athus', 'durbuy', 'houffalize', 'vielsalm', 'stavelot', 'aywaille', 'sprimont', 'esneux', 'fleron', 'soumagne',
  'vise', 'oupeye', 'grace-hollogne', 'hannut', 'wanze', 'amay', 'angleur', 'isnes', 'nivelles',
])

const FLANDRE = mots([
  'flandre', 'flanders', 'vlaanderen', 'vlaams-brabant', 'flemish brabant', 'brabant flamand', 'oost-vlaanderen',
  'west-vlaanderen', 'east flanders', 'west flanders', 'limburg', 'limbourg', 'antwerpen', 'antwerp', 'anvers',
  'gent', 'ghent', 'gand', 'brugge', 'bruges', 'leuven', 'louvain', 'mechelen', 'malines', 'hasselt', 'genk',
  'kortrijk', 'courtrai', 'oostende', 'ostende', 'ostend', 'aalst', 'alost', 'sint-niklaas', 'roeselare', 'beerse',
  'geel', 'turnhout', 'asse', 'zaventem', 'vilvoorde', 'diegem', 'machelen', 'dilbeek', 'lebbeke', 'beveren',
  'deinze', 'kruisem', 'zeebrugge', 'linkebeek', 'sint-pieters-leeuw', 'halle', 'grimbergen', 'kontich', 'mortsel',
  'lier', 'herentals', 'waregem', 'zwijnaarde', 'merelbeke', 'tienen', 'diest', 'aarschot', 'ninove', 'dendermonde',
  'lokeren', 'wetteren', 'temse', 'bornem', 'puurs', 'boom', 'edegem', 'wilrijk', 'kapellen', 'schoten',
  'brasschaat', 'zele', 'eeklo', 'ieper', 'ypres', 'menen', 'harelbeke', 'izegem', 'tielt', 'torhout', 'knokke',
  'lommel', 'maasmechelen', 'tongeren', 'sint-truiden', 'beringen', 'mol', 'westerlo', 'heist-op-den-berg',
  'groot-bijgaarden', 'wemmel', 'kraainem', 'tervuren', 'overijse', 'beersel', 'kortenberg', 'herent', 'haacht',
  'willebroek', 'hamme', 'oudenaarde', 'ronse', 'zottegem', 'destelbergen', 'lochristi', 'evergem', 'aalter',
  'oostkamp', 'wevelgem', 'hoogstraten', 'zoersel', 'heverlee', 'kessel-lo', 'nossegem', 'zellik', 'melsbroek',
  'steenokkerzeel', 'wommelgem', 'berchem', 'deurne', 'hoboken', 'merksem', 'ekeren', 'schelle', 'niel', 'rumst',
  'duffel', 'bonheiden', 'keerbergen', 'erembodegem', 'denderleeuw', 'geraardsbergen', 'ternat', 'londerzeel',
])

const LUXEMBOURG_COMMUNES = mots([
  'esch-sur-alzette', 'esch', 'mamer', 'munsbach', 'lintgen', 'findel', 'diekirch', 'bissen', 'leudelange', 'foetz',
  'belvaux', 'hesperange', 'contern', 'strassen', 'junglinster', 'grevenmacher', 'echternach', 'senningerberg',
  'merl', 'ingeldorf', 'hamm', 'bertrange', 'differdange', 'dudelange', 'ettelbruck', 'kirchberg', 'capellen',
  'windhof', 'wasserbillig', 'remich', 'mersch', 'howald', 'bettembourg', 'sandweiler', 'niederanven', 'steinsel',
  'walferdange', 'kayl', 'schifflange', 'wiltz', 'clervaux', 'vianden', 'redange', 'mondorf', 'petange', 'rodange',
  'sanem', 'mondercange', 'gasperich', 'cloche d\'or', 'grand-duche', 'grand duchy', 'luxemburg', 'belval',
  'canach', 'kehlen', 'kockelscheuer', 'mensdorf', 'oberanven', 'pommerloch', 'bascharage', 'colmar-berg', 'ehnen',
  'hosingen', 'gonderange', 'mertert', 'schieren', 'soleuvre', 'troisvierges', 'weiswampach',
])

const ETRANGER = mots([
  'usa', 'united states', 'etats-unis', 'ind', 'india', 'mumbai', 'bangalore', 'bengaluru', 'srb', 'serbia',
  'belgrade', 'col', 'colombia', 'bogota', 'israel', 'tel aviv', 'france', 'paris', 'lille', 'lyon', 'netherlands',
  'nederland', 'pays-bas', 'amsterdam', 'rotterdam', 'eindhoven', 'utrecht', 'germany', 'deutschland',
  'allemagne', 'berlin', 'munich', 'frankfurt', 'united kingdom', 'london', 'londres', 'spain', 'espagne',
  'madrid', 'barcelona', 'portugal', 'lisbon', 'lisbonne', 'poland', 'pologne', 'warsaw', 'romania', 'roumanie',
  'italy', 'italie', 'switzerland', 'suisse', 'zurich', 'geneve', 'geneva', 'ireland', 'dublin', 'canada',
  'brazil', 'bresil', 'mexico', 'singapore', 'philippines', 'egypt', 'morocco', 'maroc', 'tunisia', 'tunisie',
  'chn', 'china', 'beijing', 'sgp', 'hyderabad', 'torino', 'grand est', 'turkey', 'istanbul',
  // Issue #194 : les employeurs collectés en France, aux Pays-Bas, en
  // Allemagne et en Suisse écrivent leurs sites sans toujours le pays.
  'frankreich', 'frankrijk', 'marseille', 'toulouse', 'nantes', 'bordeaux', 'strasbourg', 'colombes',
  'issy-les-moulineaux', 'niederlande', 'den haag', 'the hague', 'leiden', 'groningen', 'breda', 'amersfoort',
  'schiphol', 'zwolle', 'duitsland', 'hamburg', 'hannover', 'neuss', 'norderstedt', 'stuttgart', 'leipzig',
  'dresden', 'munchen', 'muenchen', 'koln', 'dusseldorf', 'aachen', 'schweiz', 'zwitserland', 'schaffhausen',
  'zug', 'lausanne', 'basel', 'lugano',
])

const BELGIQUE = /(^|[^a-z])(belgium|belgique|belgie|belgien|be|bel)($|[^a-z])/

/** La région d'un code postal belge (quatre chiffres). */
function parCodePostal(code: number): Region | null {
  if (code >= 1000 && code <= 1299) return 'bruxelles'
  if (code >= 1300 && code <= 1499) return 'wallonie'
  if (code >= 1500 && code <= 3999) return 'flandre'
  if (code >= 4000 && code <= 7999) return 'wallonie'
  if (code >= 8000 && code <= 9999) return 'flandre'
  return null
}

/** La région d'un lieu, ou `null` quand on ne sait pas : jamais deviné. */
export function regionDe(lieu: string | null | undefined): Region | null {
  if (!lieu || !lieu.trim()) return null
  const l = sansAccents(lieu)
  // « L-1234 », « (LU) » : sans ambiguïté.
  if (/(^|[^a-z])l-\d{4}($|\D)/.test(l) || /\(lu\)/.test(l)) return 'luxembourg'
  const belge = BELGIQUE.test(l)
  // Un code postal à quatre chiffres ne dit la Belgique que si le lieu le dit
  // aussi, ou s'il est collé à une commune connue : le Luxembourg a les siens.
  const code = l.match(/(^|\D)(\d{4})($|\D)/)
  if (code && (belge || WALLONIE.test(l) || FLANDRE.test(l) || BRUXELLES_GENERIQUE.test(l))) {
    const r = parCodePostal(Number(code[2]))
    if (r) return r
  }
  // La province belge de Luxembourg avant le Grand-Duché.
  if (/luxembourg/.test(l) && belge) return 'wallonie'
  if (BRUXELLES_COMMUNES.test(l)) return 'bruxelles'
  if (WALLONIE.test(l)) return 'wallonie'
  if (FLANDRE.test(l)) return 'flandre'
  if (BRUXELLES_GENERIQUE.test(l)) return 'bruxelles'
  if (/luxembourg/.test(l) || LUXEMBOURG_COMMUNES.test(l)) return 'luxembourg'
  // Les codes pays à trois lettres de SuccessFactors (« Hamburg, DEU, 22117 »),
  // seulement entre virgules.
  if (ETRANGER.test(l) || /,\s*(us|de|in)\s*$/.test(l) || /,\s*(deu|fra|nld|che|usa|gbr|esp|ita|pol)\s*(,|$)/.test(l)) return 'etranger'
  return null
}

/** Vrai si l'offre passe le filtre : aucune région choisie (pas de filtre),
 *  lieu inconnu (on ne l'écarte jamais), ou région parmi celles choisies. */
export function dansRegions(lieu: string | null | undefined, choisies: readonly Region[]): boolean {
  if (choisies.length === 0) return true
  const r = regionDe(lieu)
  return r === null || choisies.includes(r)
}
